import React from "react";
import {
  getPstOffset,
  getRelativeDate,
  serviceToDisplay,
} from "../../util/dateUtils";
import {
  Textarea,
  Multiselect,
  SelectProps,
  DateRangePicker,
  NonCancelableCustomEvent,
} from "@amzn/awsui-components-react/polaris";
import { setAttributeQuery, Range } from "./searchSideBarConnector";
import {
  DATE_RANGE_I18N,
  DATE_RANGE_RELATIVE_OPTIONS,
} from "./SearchSideBar-config";
import { DateRangePickerProps } from "@amzn/awsui-components-react/polaris/date-range-picker/interfaces";

function optionsFromCsv(labelGetter: (value: string) => string, csv?: string) {
  if (csv === undefined) {
    return [];
  }
  const values = csv.split(",").map((s) => s.trim());
  return values
    .filter((value) => value !== "")
    .map((value) => ({
      value: value,
      label: labelGetter(value),
    }));
}

export const StringField = (
  placeholder: string,
  attributeName: string,
  value: string,
  dispatch: any
) => {
  return (
    <Textarea
      placeholder={placeholder}
      onChange={(event) => {
        dispatch(
          setAttributeQuery({
            attribute: attributeName,
            queryState: {
              value: event.detail.value,
            },
          })
        );
      }}
      value={value}
    />
  );
};

export const MultiselectField = (
  options: SelectProps.Options,
  placeholder: string,
  attributeName: string,
  value: string,
  dispatch: any,
  status?: "pending" | "loading" | "finished" | "error",
  loadingText?: string,
  retryFunction?: any,
  labelGetter: (value: string) => string = (v) => v,
  virtualScroll: boolean = false
) => {
  return (
    <Multiselect
      virtualScroll={options.length >= 500 || virtualScroll}
      statusType={status}
      loadingText={loadingText}
      placeholder={placeholder}
      filteringType="auto"
      options={options}
      selectedOptions={optionsFromCsv(labelGetter, value)}
      onChange={(event) => {
        const serializedOptions = event.detail.selectedOptions
          .map((o) => o.value)
          .join(",");
        dispatch(
          setAttributeQuery({
            attribute: attributeName,
            queryState: {
              value: serializedOptions,
            },
          })
        );
      }}
      errorText={"Something went wrong."}
      recoveryText={"Retry"}
      onLoadItems={({ detail }) => {
        if (detail.samePage && retryFunction) {
          dispatch(retryFunction());
        }
      }}
    />
  );
};

/**
 * Convert a Cloudscape `DateRangePicker` absolute value into the
 * PST-normalised `{lower, upper}` range the app stores. The lower
 * bound is set to 00:00 PST of the start date; the upper bound to
 * 23:59:59 PST of the end date, so a same-day range covers the full
 * calendar day.
 */
const absoluteToRange = (value: DateRangePickerProps.AbsoluteValue): Range => {
  const [start, end] = [new Date(value.startDate), new Date(value.endDate)].map(
    (d) => getRelativeDate(d, -getPstOffset(d), "minute")
  );
  return {
    lower: start,
    upper: getRelativeDate(end, 24 * 60 * 60 - 1, "second"),
  };
};

/**
 * Convert a relative range ("last N units") into a concrete
 * `{lower, upper}` range anchored at now.
 */
const relativeToRange = (value: DateRangePickerProps.RelativeValue): Range => {
  const now = new Date();
  const target = getRelativeDate(now, value.amount, value.unit);
  return value.amount > 0
    ? { lower: now, upper: target }
    : { lower: target, upper: now };
};

const validateRange: DateRangePickerProps.ValidationFunction = (range) => {
  if (range?.type === "absolute") {
    const [startDateWithoutTime] = range.startDate.split("T");
    const [endDateWithoutTime] = range.endDate.split("T");
    if (!startDateWithoutTime || !endDateWithoutTime) {
      return {
        valid: false,
        errorMessage:
          "The selected date range is incomplete. Select a start and end date for the date range.",
      };
    }
  } else if (range?.type === "relative") {
    if (isNaN(range.amount)) {
      return {
        valid: false,
        errorMessage:
          "The selected date range is incomplete. Specify a duration for the date range.",
      };
    }
  }
  return { valid: true };
};

export interface DateRangeFieldProps {
  /** Current range, or undefined for empty. */
  value?: Range;
  /**
   * Fired with a concrete `{lower, upper}` when the user picks a
   * range and clicks the picker's Apply, or `null` when they clear.
   * Callers own storage — Redux dispatch in the sidebar, Cloudscape
   * PropertyFilter form callback for the shared bar.
   */
  onChange: (range: Range | null) => void;
}

/**
 * Thin wrapper over Cloudscape's `DateRangePicker`. Handles the
 * app-specific normalisation (PST offset, end-of-day upper) and the
 * absolute/relative → `{lower, upper}` conversion, and exposes a
 * plain `onChange(range | null)` callback so callers stay agnostic
 * of Redux vs Cloudscape's property-filter form contract.
 */
export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  value,
  onChange,
}) => {
  const handleDateRangePickerChange = (
    event: NonCancelableCustomEvent<DateRangePickerProps.ChangeDetail>
  ): void => {
    const dateValue = event.detail.value;
    if (dateValue === null) {
      onChange(null);
      return;
    }
    switch (dateValue.type) {
      case "absolute":
        onChange(absoluteToRange(dateValue));
        return;
      case "relative":
        onChange(relativeToRange(dateValue));
        return;
    }
  };

  const pickerValue: DateRangePickerProps.Value = {
    startDate: value ? serviceToDisplay(value.lower, "yyyy-MM-DD") : "",
    endDate: value ? serviceToDisplay(value.upper, "yyyy-MM-DD") : "",
    type: "absolute",
  };

  return (
    <DateRangePicker
      expandToViewport
      dateOnly
      value={pickerValue}
      relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
      isValidRange={validateRange}
      i18nStrings={DATE_RANGE_I18N}
      onChange={handleDateRangePickerChange}
      timeOffset={0}
    />
  );
};
